import * as React from 'react'
import { Form, Input, InputNumber, message, Modal, Select, TreeSelect } from 'antd'
import { useSearchParams } from 'react-router-dom'
import AppLayout from './AppLayout'
import Ret00cSidebar, { useRet00cSidebarTree } from '../ret00cs/Ret00cSidebar'
import { Ret00cGroupFilterProvider, useRet00cGroupFilter } from '../ret00cs/useRet00cGroupFilter'
import { createRet00cd } from 'components/common/services/ret00cds'

interface GroupFormValues {
  name: string
  parent_id?: number
  sort_no?: number
  status?: string
  remark?: string
}

function SiderWithGroupSync(props: {
  overviewRefreshDeps?: unknown[]
  groupRefreshDeps?: unknown[]
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedGroupId, setSelectedGroupId } = useRet00cGroupFilter()

  React.useEffect(() => {
    const v = searchParams.get('ret00cd_id')
    setSelectedGroupId(v ? Number(v) : undefined)
  }, [searchParams, setSelectedGroupId])

  const handleSelectGroup = React.useCallback(
    (groupId?: number) => {
      setSelectedGroupId(groupId)
      const params: Record<string, string> = {}
      searchParams.forEach((val, key) => {
        params[key] = val
      })
      if (groupId !== undefined) {
        params.ret00cd_id = String(groupId)
      } else {
        delete params.ret00cd_id
      }
      setSearchParams(params)
    },
    [searchParams, setSearchParams, setSelectedGroupId],
  )

  return (
    <Ret00cSidebar
      selectedGroupId={selectedGroupId}
      onSelectGroup={handleSelectGroup}
      overviewRefreshDeps={props.overviewRefreshDeps}
      groupRefreshDeps={props.groupRefreshDeps}
    />
  )
}

export default function Ret00csModuleLayout(): JSX.Element {
  const openRef = React.useRef<() => void>(() => undefined)
  const reloadTreeRef = React.useRef<() => void>(() => undefined)

  const handleOpenGroupModal = React.useCallback(() => {
    openRef.current?.()
  }, [])

  const handleReloadTree = React.useCallback(() => {
    reloadTreeRef.current?.()
  }, [])

  return (
    <Ret00cGroupFilterProvider onOpenGroupModal={handleOpenGroupModal} onReloadTree={handleReloadTree}>
      <InnerModuleLayoutWithOpenRef openRef={openRef} reloadTreeRef={reloadTreeRef} />
    </Ret00cGroupFilterProvider>
  )
}

function InnerModuleLayoutWithOpenRef(props: {
  openRef: React.MutableRefObject<() => void>
  reloadTreeRef: React.MutableRefObject<() => void>
}): JSX.Element {
  const [groupForm] = Form.useForm<GroupFormValues>()
  const [groupModalOpen, setGroupModalOpen] = React.useState(false)
  const [reloadTick, setReloadTick] = React.useState(0)
  const { selectedGroupId, setSelectedGroupId } = useRet00cGroupFilter()
  const { groupTreeOptions, reloadOverviewTree, reloadGroupTree } = useRet00cSidebarTree([reloadTick])

  const openGroupModal = React.useCallback(() => {
    groupForm.setFieldsValue({
      name: '',
      parent_id: selectedGroupId,
      sort_no: 0,
      status: 'enabled',
      remark: '',
    })
    setGroupModalOpen(true)
  }, [groupForm, selectedGroupId])

  React.useImperativeHandle(props.openRef, () => openGroupModal, [openGroupModal])

  const reloadTree = React.useCallback(() => {
    setReloadTick((t) => t + 1)
  }, [])

  React.useImperativeHandle(props.reloadTreeRef, () => reloadTree, [reloadTree])

  const handleCreateGroup = async () => {
    const values = await groupForm.validateFields()
    const response: any = await createRet00cd(values)
    const createdId = response?.data?.data?.item?.id

    message.success('分组新增成功')
    setGroupModalOpen(false)
    setReloadTick((t) => t + 1)
    await Promise.all([reloadGroupTree(), reloadOverviewTree()])

    if (createdId) {
      setSelectedGroupId(createdId)
    }
  }

  return (
    <>
      <AppLayout
        sidebar={
          <SiderWithGroupSync
            overviewRefreshDeps={[reloadTick]}
            groupRefreshDeps={[reloadTick]}
          />
        }
        moduleKey="ret00cs"
      />

      <Modal
        open={groupModalOpen}
        title="新增分组"
        onCancel={() => setGroupModalOpen(false)}
        onOk={handleCreateGroup}
        destroyOnClose
        getContainer={() => document.body}
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item
            name="name"
            label="分组名称"
            rules={[{ required: true, message: '请输入分组名称' }]}
          >
            <Input placeholder="例如：订单 / 支付 / 对账" />
          </Form.Item>
          <Form.Item name="parent_id" label="上级分组">
            <TreeSelect
              style={{ width: '100%' }}
              treeData={groupTreeOptions}
              allowClear
              treeDefaultExpandAll
              placeholder="不选则为根分组"
              treeNodeFilterProp="title"
              showSearch
              getPopupContainer={(triggerNode: any) => triggerNode.parentElement || document.body}
            />
          </Form.Item>
          <Form.Item name="sort_no" label="排序">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              options={[
                { label: '启用', value: 'enabled' },
                { label: '停用', value: 'disabled' },
              ]}
            />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
